import { useCallback, useEffect, useMemo } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import { DataLayer } from "@/types/dataLayer";
import { useState } from "react";
import useRequest from "@/lib/request/useRequest";
import { removeLayersIfExists, removeSourcesIfExists } from "@/lib/utils/map";
import { ImageSource, RasterLayerSpecification } from "mapbox-gl";
import SimpleLayer from "./SimpleLayer";
import { Select, Selection, SelectItem } from "@heroui/react";

function getDataLayerSource(id: number) {
    return `layers-data-layer-${id}`;
}
function getDataLayerImageLayer(id: number, isVisible: boolean) {
    return {
        id: `layers-data-layer-image-${id}`,
        type: "raster",
        source: getDataLayerSource(id),
        layout: {
            visibility: isVisible ? "visible" : "none",
        },
        paint: {
            "raster-fade-duration": 0,
        },
    } as RasterLayerSpecification;
}

function Row({
    layer,
    downloadDataLayer,
}: {
    layer: DataLayer;
    downloadDataLayer: (id: number, name: string) => void;
}) {
    const { fieldBbox } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const { map, mapLoaded } = useMap();

    const [selectedDataLayer, setSelectedDataLayer] = useState<Selection>(
        new Set(),
    );

    const [dataLayerId, attributeId] = useMemo(() => {
        if (selectedDataLayer != "all" && !selectedDataLayer.size)
            return [null, null];
        const [dataLayerId, attributeId] = (
            Array.from(selectedDataLayer)[0] as string
        )?.split(".") ?? [null, null];
        return [dataLayerId, attributeId];
    }, [selectedDataLayer]);

    const dataLayerImageQuery = useGetRequest<Blob>({
        url: `data_layers/${dataLayerId}/attributes/${attributeId}/`,
        responseType: "blob",
        enabled: !!dataLayerId,
    });

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !dataLayerImageQuery.data
            ? null
            : window.URL.createObjectURL(dataLayerImageQuery.data as Blob);

        if (imageUrl) {
            const imSource = map.getSource(
                getDataLayerSource(layer.id),
            ) as ImageSource;
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: fieldBbox,
                    });
                }
            } else {
                map.addSource(getDataLayerSource(layer.id), {
                    type: "image",
                    url: imageUrl,
                    coordinates: fieldBbox,
                });
                map.addLayer(getDataLayerImageLayer(layer.id, isVisible));
            }
        } else {
            removeLayersIfExists(map, [
                getDataLayerImageLayer(layer.id, isVisible).id,
            ]);
            removeSourcesIfExists(map, [getDataLayerSource(layer.id)]);
        }
    }, [
        dataLayerImageQuery.data,
        fieldBbox,
        layer.id,
        map,
        mapLoaded,
        isVisible,
    ]);

    return (
        <SimpleLayer
            key={layer.id}
            name={layer.name}
            layerNames={[getDataLayerImageLayer(layer.id, isVisible).id]}
            onDownload={() => downloadDataLayer(layer.id, layer.name)}
            onValueChange={(value) => setIsVisible(value)}
        >
            {() => {
                return (
                    <Select
                        aria-label="Select Data Layer"
                        onSelectionChange={setSelectedDataLayer}
                        selectedKeys={selectedDataLayer}
                        className="mt-2"
                        size="sm"
                    >
                        {layer.attributes.map((attribute) => (
                            <SelectItem key={layer.id + "." + attribute.id}>
                                {attribute.name}
                            </SelectItem>
                        ))}
                    </Select>
                );
            }}
        </SimpleLayer>
    );
}

function DataLayersMenu() {
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest, downloadFile } = useRequest();

    const dataLayerQuery = useGetRequest<DataLayer[]>({
        url: `fields/${fieldId}/data_layers/`,
        enabled: !!fieldId,
    });

    const downloadDataLayer = useCallback(
        async (id: number, name: string) => {
            if (!id) return;
            await downloadFile(`data_layers/${id}/`, name);
        },
        [downloadFile],
    );

    return dataLayerQuery?.data?.map((layer) => (
        <Row
            key={layer.id}
            layer={layer}
            downloadDataLayer={downloadDataLayer}
        />
    ));
}

export default DataLayersMenu;
